
import { injectable, inject } from 'inversify';
import { ITombolaRouletteAuthenticationResponse, CatchUpData, CallsData } from '@/components/game/_domain';
import { BaseGameController } from '@/components/game/implementations/_base/_domain/controller/game-controller';
import { Prop } from 'vue-property-decorator';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IConfigurationService } from '@/app-modules/config';
import { IServerServiceFactory } from '@/app-modules/server-service';
import { IGameRepresentation, IGame, GameIdentity, IGameConfiguration, GameState, GameCallboard } from '@/components/game/_domain/models';
import { IWinnersTrackerService } from '@/components/game/_domain/modules/winners-tracker-service';
import { PrizeWinnerData } from '@/components/game/_domain/server/prize-winner/prize-winner-data';
import { IErrorService } from '@/app-modules/error';

interface ITombolaRouletteGameRepresentation extends IGameRepresentation {
  callhistory?: string[];
}

class TombolaRouletteGame implements IGame {
  public constructor(public readonly gameIdentity: GameIdentity, public readonly gameConfiguration: IGameConfiguration) {
    this.connectionDebugInfo = 'Disconnected';
    this.state = GameState.InPlay;
  }

  public connectionDebugInfo: string;
  public state: GameState;
  public representation?: ITombolaRouletteGameRepresentation;

  public updateRepresentation(newGameRepresentation: Partial<ITombolaRouletteGameRepresentation>): void {
    this.representation = Object.assign({}, this.representation, newGameRepresentation || {});
  }
}

@injectable()
class TombolaRouletteController extends BaseGameController {
  protected game: TombolaRouletteGame;

  public constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IServerServiceFactory) serverServiceFactory: IServerServiceFactory,
    @inject(SERVICE_IDENTIFIERS.IWinnersTrackerService) winnersTrackerService: IWinnersTrackerService,
    @inject(SERVICE_IDENTIFIERS.IErrorService) errorService: IErrorService
  ) {
    super(configurationService, serverServiceFactory, winnersTrackerService, errorService);
  }

  public initialise(gameIdentity: GameIdentity) {
    this.game = this.getInitialGame(gameIdentity);
    this.initialiseGameServerConnection();
    this.initialiseGameRetryPolicySettings();
    this.initialiseGameServerService();
  }

  protected subscribeToGameEvents(): void {
    this.serverSubscriptionStore.push(
      this.gameServerService.subscribeCatchUp(this.handleCatchUpMessage.bind(this)),
      this.gameServerService.subscribeWinners(this.handleWinnersMessage.bind(this)),
      this.gameServerService.subscribeReset(this.handleResetMessage.bind(this)),
      this.gameServerService.subscribeCalls(this.handleCallsMessage.bind(this)),
      this.gameServerService.subscribeNextGame(this.handleNextGameMessage.bind(this)),
      this.gameServerService.subscribePrizeWinner(this.handlePrizeWinnerMessage.bind(this))
    );
  }

  // overriding because we are getting a prizeWinner instead of winners notification which does not call for Display Of winners stage of the game - FM
  protected handlePrizeWinnerMessage(payload: PrizeWinnerData): void {
    const displayWinnersForInMilliseconds = 20000;
    const currentWinnerSets = this.game.representation.winners || [];
    const payloadWinnerSets = this.getGameStageWinnerSetsFromPrizeWinnerData(payload);
    this.changeGameRepresentation({
      winners: this.mergeGameStageWinnerSets(currentWinnerSets, payloadWinnerSets),
      isJackpotWon: payload.jackpot
    });
    this.notifyGameWon();
    this.trackPrizeWinners(payloadWinnerSets);
    this.changeGameState(GameState.WinnersDisplayed);

    setTimeout(() => {
      this.changeGameState(GameState.Purchasing);
    }, displayWinnersForInMilliseconds);
  }

  protected getInitialGame(gameIdentity: GameIdentity): TombolaRouletteGame {
    const gameConfig = this.getGameConfiguration(gameIdentity);
    const maxNumberOfCallsPerGame = gameConfig.maxNumberOfCallsPerGame;
    const game = new TombolaRouletteGame(gameIdentity, gameConfig);
    game.representation = {
      callboard: new GameCallboard(maxNumberOfCallsPerGame)
    };
    return game;
  }

  protected handleCallsMessage(payload: CallsData): void {
    this.game.representation.callboard.update([payload.callValue], payload.callNumber);
    this.game.representation.callhistory = this.updateLastCallsArray(payload.callValue.toString());
    this.notifyGameRepresentationChanged();
  }

  protected handleAuthorisationMessage(payload: ITombolaRouletteAuthenticationResponse): void {
    this.changeGameConnectionState('Authorised');
    this.changeGameRepresentation({
      callhistory: this.getCallHistoryArray(payload.callHistory)
    });
  }

  protected handleCatchUpMessage(payload: CatchUpData): void {
    this.game.representation.callboard.update(payload.callValues);
    this.changeGameRepresentation({
      gameId: payload.gameNo,
      numberOfPlayers: payload.numberOfPlayers,
      prizeStructure: payload.prizeStructure,
    });
  }

  protected changeGameRepresentation(newGameRepresentation: Partial<ITombolaRouletteGameRepresentation>): void {
    this.game.updateRepresentation(newGameRepresentation);
    this.notifyGameRepresentationChanged();
  }

  public updateLastCallsArray(newCall: string): string[] {
    const newCallHistory = this.game.representation.callhistory || [];
    if (newCallHistory.length > 11) {
      newCallHistory.splice(0, 1);
    }
    newCallHistory.push(newCall);

    return newCallHistory;
  }

  private getCallHistoryArray(callHistory: string): string[] {
    const callHistoryArray: string[] = [];
    for (let i = 0; i < callHistory.length; i += 2) {
      callHistoryArray.push(Number(callHistory.substr(i, 2)).toString());
    }
    return callHistoryArray;
  }
}

export { TombolaRouletteController, ITombolaRouletteGameRepresentation };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/tombola-roulette/_domain/controller/game-controller.ts